import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  selectCurrentPatient,
  setCurrentPatient
} from '../../../reducers/currentPatientReducer'
import {
  selectCurrentVisits,
  setVisits,
  sortVisits
} from '../../../reducers/currentVisitsReducer'
import { getPatientService, getVisitService } from '../../../services/Services'
import { type SortOrderType } from '../types'

export function usePatientData(
  patientId: string | undefined,
  sortOrder: SortOrderType
) {
  const dispatch = useAppDispatch()
  const currentPatient = useAppSelector(selectCurrentPatient)
  const currentVisits = useAppSelector(selectCurrentVisits)

  const [visitsLoading, setVisitsLoading] = useState(false)
  const [summary, setSummary] = useState<string>('')
  const [summaryLoading, setSummaryLoading] = useState(false)
  const sortOrderRef = useRef(sortOrder)
  sortOrderRef.current = sortOrder

  const fetchOnePatient = useCallback(async () => {
    if (patientId && currentPatient.id !== patientId) {
      const patient = await getPatientService().getOnePatient({ patientId })
      if (patient) {
        dispatch(setCurrentPatient(patient))
      }
    }
  }, [patientId, dispatch, currentPatient.id])

  const fetchVisits = useCallback(async () => {
    if (patientId) {
      setSummary('')
      setVisitsLoading(true)
      try {
        const _visits = await getVisitService().getVisits(patientId)
        dispatch(setVisits(_visits))
        dispatch(sortVisits(sortOrderRef.current))
      } finally {
        setVisitsLoading(false)
      }
    }
  }, [patientId, dispatch])

  useEffect(() => {
    dispatch(sortVisits(sortOrder))
  }, [dispatch, sortOrder])

  // Fetch visits whenever currentPatient changes (patient already in store)
  useEffect(() => {
    if (currentPatient) {
      fetchVisits()
    }
  }, [currentPatient, fetchVisits])

  // Fetch patient if patientId differs from what's in store (direct URL navigation)
  useEffect(() => {
    if (patientId && currentPatient.id !== patientId) {
      fetchOnePatient()
    }
  }, [currentPatient.id, fetchOnePatient, patientId])

  const handleGenerateSummary = useCallback(async () => {
    setSummaryLoading(true)
    try {
      const summaryText = await getVisitService().getAISummary(
        currentPatient,
        currentVisits
      )
      setSummary(summaryText)
    } finally {
      setSummaryLoading(false)
    }
  }, [currentPatient, currentVisits])

  return {
    visitsLoading,
    summary,
    summaryLoading,
    handleGenerateSummary
  }
}
